import React from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  LayoutGrid,
  Music2,
  Volume2,
  ZoomIn,
  ZoomOut,
  ListMusic,
  Headphones,
  Pause,
  Loader2,
} from "lucide-react";

const IconBtn = ({ onClick, disabled, label, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    data-testid={testId}
    className="debossed-btn flex h-11 w-11 items-center justify-center rounded-full text-ink-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
  >
    {children}
  </button>
);

export const ReaderControls = ({
  visible,
  onPrev,
  onNext,
  canPrev,
  canNext,
  pageLabel,
  progress,
  onToggleFullscreen,
  isFullscreen,
  onOpenScrub,
  musicOn,
  onToggleMusic,
  onOpenMusicPanel,
  musicPanelOpen = false,
  volume = 0.42,
  onVolumeChange,
  zoom = 1,
  onZoomIn,
  onZoomOut,
  canZoomIn = true,
  canZoomOut = false,
  narrateEnabled = false,
  narrateState = "idle",
  onToggleNarrate,
}) => {
  return (
    <motion.div
      initial={false}
      animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 18 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-5 safe-bottom safe-x"
      style={{ pointerEvents: visible ? "auto" : "none" }}
      data-testid="reader-controls"
    >
      <div className="debossed relative flex max-w-[calc(100vw-1.5rem)] items-center gap-1.5 rounded-full px-2.5 py-2 sm:gap-2 sm:px-3">
        {/* progress line along the top edge of the pill */}
        <div className="absolute -top-[6px] left-6 right-6 h-[2px] overflow-hidden rounded-full bg-ivory-400/60">
          <div
            className="h-full bg-gold transition-[width] duration-500 ease-out"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>

        <IconBtn onClick={onPrev} disabled={!canPrev} label="Previous page" testId="prev-page-btn">
          <ChevronLeft strokeWidth={1.4} className="h-5 w-5" />
        </IconBtn>

        <button
          type="button"
          onClick={onOpenScrub}
          data-testid="page-indicator-btn"
          aria-label="Browse pages"
          title="Browse pages"
          className="debossed-btn min-w-[64px] rounded-full px-3 py-1.5 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:min-w-[92px]"
        >
          <span className="block font-display text-[15px] leading-none text-ink num-old" data-testid="page-indicator-value">
            {pageLabel}
          </span>
        </button>

        <IconBtn onClick={onNext} disabled={!canNext} label="Next page" testId="next-page-btn">
          <ChevronRight strokeWidth={1.4} className="h-5 w-5" />
        </IconBtn>

        <span className="mx-1 hidden h-6 w-px bg-ivory-400/70 sm:inline-block" aria-hidden="true" />

        <IconBtn onClick={onZoomOut} disabled={!canZoomOut} label="Zoom out" testId="zoom-out-btn">
          <ZoomOut strokeWidth={1.4} className="h-[18px] w-[18px]" />
        </IconBtn>
        <span
          className="hidden min-w-[44px] text-center font-display text-[13px] leading-none text-ink num-old sm:inline-block"
          data-testid="zoom-level"
        >
          {Math.round(zoom * 100)}%
        </span>
        <IconBtn onClick={onZoomIn} disabled={!canZoomIn} label="Zoom in" testId="zoom-in-btn">
          <ZoomIn strokeWidth={1.4} className="h-[18px] w-[18px]" />
        </IconBtn>

        <span className="mx-1 hidden h-6 w-px bg-ivory-400/70 sm:inline-block" aria-hidden="true" />

        <button
          type="button"
          onClick={onToggleMusic}
          aria-label={musicOn ? "Turn off ambient music" : "Turn on ambient music"}
          aria-pressed={musicOn}
          title={musicOn ? "Ambient music: on" : "Ambient music: off"}
          data-testid="music-toggle-btn"
          className={`debossed-btn flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${
            musicOn ? "text-gold-deep" : "text-ink-soft"
          }`}
        >
          {musicOn ? (
            <Volume2 strokeWidth={1.4} className="h-[18px] w-[18px]" />
          ) : (
            <Music2 strokeWidth={1.4} className="h-[18px] w-[18px]" />
          )}
        </button>

        <button
          type="button"
          onClick={onOpenMusicPanel}
          aria-label="Choose a track"
          aria-pressed={musicPanelOpen}
          title="Choose a track"
          data-testid="music-panel-btn"
          className={`debossed-btn hidden h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 sm:flex ${
            musicPanelOpen ? "text-gold-deep" : "text-ink-soft"
          }`}
        >
          <ListMusic strokeWidth={1.4} className="h-[18px] w-[18px]" />
        </button>

        {narrateEnabled && (
          <button
            type="button"
            onClick={onToggleNarrate}
            aria-label={narrateState === "playing" ? "Stop reading aloud" : "Read this page aloud"}
            aria-pressed={narrateState === "playing"}
            title={narrateState === "playing" ? "Stop reading aloud" : "Read this page aloud"}
            data-testid="narrate-btn"
            disabled={narrateState === "loading"}
            className={`debossed-btn flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${
              narrateState === "playing" ? "text-gold-deep" : "text-ink-soft"
            }`}
          >
            {narrateState === "loading" ? (
              <Loader2 strokeWidth={1.6} className="h-[18px] w-[18px] animate-spin" />
            ) : narrateState === "playing" ? (
              <Pause strokeWidth={1.6} className="h-[18px] w-[18px]" />
            ) : (
              <Headphones strokeWidth={1.4} className="h-[18px] w-[18px]" />
            )}
          </button>
        )}

        <input
          type="range"
          min={0}
          max={1}
          step={0.02}
          value={volume}
          onChange={(e) => onVolumeChange && onVolumeChange(parseFloat(e.target.value))}
          aria-label="Music volume"
          title="Music volume"
          data-testid="volume-slider"
          className="volume-slider hidden h-1.5 w-[68px] cursor-pointer appearance-none rounded-full sm:block sm:w-[84px]"
          style={{
            background: `linear-gradient(90deg, #C5A059 0%, #C5A059 ${Math.round(
              volume * 100
            )}%, rgba(160,140,100,0.3) ${Math.round(volume * 100)}%, rgba(160,140,100,0.3) 100%)`,
          }}
        />

        <span className="hidden sm:flex">
          <IconBtn onClick={onOpenScrub} label="Page thumbnails" testId="open-thumbnails-btn">
            <LayoutGrid strokeWidth={1.4} className="h-[18px] w-[18px]" />
          </IconBtn>
        </span>

        <span className="hidden sm:flex">
          <IconBtn
            onClick={onToggleFullscreen}
            label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            testId="fullscreen-btn"
          >
            {isFullscreen ? (
              <Minimize2 strokeWidth={1.4} className="h-[18px] w-[18px]" />
            ) : (
              <Maximize2 strokeWidth={1.4} className="h-[18px] w-[18px]" />
            )}
          </IconBtn>
        </span>
      </div>
    </motion.div>
  );
};
